import { FC, useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import { BUSINESS_TOOL_SECTIONS } from 'constants/businessTool';
import { IUnitedSubSections } from 'constants/businessToolSections';
import { useBreakpoints } from 'hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessPlanChapters,
  getBusinessPlanType,
  getBusinessToolChapterSelector,
} from 'store/useBusinessToolStore/selectors';

import { ReactComponent as FilledSectionIcon } from 'assets/icons/FilledSectionIcon.svg';
import { ReactComponent as LockIcon } from 'assets/icons/LockIcon.svg';

import { DoubleCircularProgress } from 'components/_basic/DoubleCircularProgress';
import { useCalculateFilledPercentage } from 'components/BusinessToolForm/components/SectionsNavigationItem/hooks/useCalculateFilledPercentage';

import { getColorForProgress } from 'helpers/businessTool';

import { DesktopView } from './DesktopView';
import { MobileView } from './MobileView';
import { ISectionsNavigationItemProps } from './types';

import styles from './SectionsNavigationItem.module.scss';

const getSectionIcon = ({ disabled, percent }: { disabled: boolean; percent: number }) => {
  if (disabled)
    return (
      <Box component="span" className={styles.lockIconContainer}>
        <LockIcon />
      </Box>
    );

  return percent === 100 ? (
    <FilledSectionIcon />
  ) : (
    <DoubleCircularProgress color={getColorForProgress(percent)} value={percent} size={20} />
  );
};

export const SectionsNavigationItem: FC<ISectionsNavigationItemProps> = ({
  activeSection,
  setActiveSection,
  activeSubsection,
  setActiveSubsection,
  option,
  scrollSections,
}) => {
  const { label, name, disabled = false, items = [], isIconToTheRight } = option;
  const { isMobile } = useBreakpoints();

  const handleSectionClick = () => {
    scrollSections?.();
    setActiveSection(name);
    setActiveSubsection(!items.length ? null : items[0].id);
  };

  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const getBusinessToolChapter = useBusinessToolStore(getBusinessToolChapterSelector);
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const global = getBusinessToolChapter(BUSINESS_TOOL_SECTIONS.GLOBAL);

  const percent = useCalculateFilledPercentage(businessPlanType, businessPlanChapters, global, name);

  const handleSubsectionItemClick = (itemId: IUnitedSubSections) => {
    setActiveSection(name);
    setActiveSubsection(itemId);
  };

  const ViewComponent = isMobile ? MobileView : DesktopView;

  useEffect(() => {
    if (!activeSubsection && name === activeSection) {
      setActiveSubsection(!items.length ? null : items[0].id);
    }
  }, [activeSubsection, activeSection, items]);

  const icon = useMemo(() => {
    return getSectionIcon({ disabled, percent });
  }, [disabled, percent]);

  return (
    <ViewComponent
      isSectionActive={activeSection === name}
      isDisabled={disabled}
      subsections={items}
      activeSubsection={activeSubsection}
      icon={icon}
      onSectionClick={handleSectionClick}
      onSubsectionItemClick={handleSubsectionItemClick}
      label={label}
      isIconToTheRight={isIconToTheRight}
    />
  );
};
